/**
 * SaveDocButton — 2026-02 Document Auto-Save MVP.
 *
 * Wraps POST /api/document-archive/save so any surface can drop in a
 * one-click "save PDF (or zip of PDFs) with the canonical Access-style
 * filename baked in".
 *
 * Two modes:
 *   1. Single doc (default) — <SaveDocButton appraisalId="..." docKind="quote_package" />
 *   2. Batch — <SaveDocButton appraisalId="..." docKinds={['appraisal_confirmation','appraisal_authorisation']} groupLabel="Appraisal Docs" />
 *
 * Renders as a compact secondary button by default; pass `variant="ghost"`
 * for the tight inline placements next to Preview buttons on builder
 * headers and the components tab.
 *
 * Filename honours operator overrides from /admin/pdf-filename-templates —
 * the backend resolves the final name via Content-Disposition and we
 * feed it into an anchor download so Chrome writes the file with the
 * exact filename the operator picked.
 */
import React, { useState } from 'react';
import { Save, Loader2 } from 'lucide-react';
import axios from 'axios';
import { Button } from './ui/button';
import { toast } from 'sonner';

// Local axios instance — mirrors the pattern used by WorkflowRunnerPage
// and every other page in the app. Keeps this component self-contained.
const _api = axios.create({ baseURL: `${process.env.REACT_APP_BACKEND_URL}/api` });
_api.interceptors.request.use((cfg) => {
  const t = localStorage.getItem('access_token');
  if (t) cfg.headers.Authorization = `Bearer ${t}`;
  return cfg;
});

// Extract filename from Content-Disposition header (handles UTF-8 filename*
// per RFC 5987 with plain filename= fallback for older browsers).
function _filenameFromDisposition(header, fallback) {
  if (!header) return fallback;
  const utf8 = /filename\*=UTF-8''([^;\s]+)/i.exec(header);
  if (utf8 && utf8[1]) {
    try {
      return decodeURIComponent(utf8[1]);
    } catch {
      /* fall through */
    }
  }
  const plain = /filename="?([^"]+)"?/i.exec(header);
  return (plain && plain[1]) || fallback;
}

export default function SaveDocButton({
  appraisalId,
  docKind,           // single-doc convenience
  docKinds,          // multi-doc; overrides docKind if set
  groupLabel,        // used in zip filename for multi-doc saves
  label = 'Save PDF',
  size = 'sm',
  variant = 'outline',
  className = '',
  disabled = false,
  testId,
  expandChildren = true,  // false = "this appraisal only" mode
  onSaved,           // optional callback(filename) after success
}) {
  const [saving, setSaving] = useState(false);

  const kinds = docKinds && docKinds.length ? docKinds : (docKind ? [docKind] : []);

  const handleClick = async () => {
    if (!appraisalId) {
      toast.error('Missing appraisal reference — reload the page and try again.');
      return;
    }
    if (kinds.length === 0) {
      toast.error('No document type specified.');
      return;
    }
    if (kinds.some(k => k === 'quotation_detailed' || k === 'painting_quotation')) {
      try {
        const { data: pf } = await _api.get(`/appraisals/${appraisalId}/child-preflight`);
        if (pf?.is_child) {
          if (pf.foreign_lines?.length || pf.parity?.ok === false) {
            toast.error(pf.foreign_lines?.length ? `Blocked: ${pf.foreign_lines.length} foreign line(s) on this quote — use "Remove foreign lines" on the banner.` : `Blocked: ${pf.parity.detail}`);
            return;
          }
          const warn = [];
          if (pf.kind === 'builder' && (pf.diagram_stale || pf.condition_diffs?.length)) warn.push('• Diagram / roof notes are out of date vs the parent');
          if (pf.zero_lines?.length) warn.push(`• ${pf.zero_lines.length} line(s) priced at $0`);
          if (pf.manual_lines?.length) warn.push(`• ${pf.manual_lines.length} manual line(s) will be included: ${pf.manual_lines.map(l => `${l.component_type} ${l.type_name}`).join(', ')}`);
          if (pf.kind === 'builder' && !pf.quotation_type) warn.push('• No Quotation Type picked');
          if (warn.length && !window.confirm(`Pre-flight check before saving this quote PDF:\n\n${warn.join('\n')}\n\nSave the PDF anyway?`)) return;
        }
      } catch { /* preflight unavailable — backend still guards */ }
    }
    setSaving(true);
    try {
      const resp = await _api.post(
        '/document-archive/save',
        {
          appraisal_id: appraisalId,
          doc_kinds: kinds,
          group_label: groupLabel || 'Documents',
          expand_children: expandChildren,
        },
        { responseType: 'blob', timeout: 60000 },
      );
      const filename = _filenameFromDisposition(
        resp.headers?.['content-disposition'] || resp.headers?.get?.('content-disposition'),
        `RTR-${appraisalId.slice(-6)}.pdf`,
      );
      // Trigger browser download → operator's Chrome download folder
      const url = window.URL.createObjectURL(resp.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`Saved ${filename}`);
      window.dispatchEvent(new Event('rtr:child-quote-refresh'));
      if (onSaved) onSaved(filename);
    } catch (err) {
      // Blob-response errors need reading the blob back to JSON for the
      // detail message; fall back to a generic error otherwise.
      let msg = 'Save failed — please try again.';
      const data = err?.response?.data;
      if (data instanceof Blob) {
        try {
          const text = await data.text();
          const parsed = JSON.parse(text);
          msg = parsed?.detail || parsed?.message || msg;
        } catch { /* keep default */ }
      } else if (typeof data === 'string') {
        msg = data;
      } else if (data?.detail) {
        msg = data.detail;
      }
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Button
      type="button"
      size={size}
      variant={variant}
      className={className}
      onClick={handleClick}
      disabled={disabled || saving || !appraisalId}
      data-testid={testId || `save-doc-btn-${kinds.join('-')}`}
    >
      {saving ? (
        <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />
      ) : (
        <Save className="w-3.5 h-3.5 mr-1.5" />
      )}
      {saving ? 'Saving…' : label}
    </Button>
  );
}
